import { forwardRef, HTMLAttributes, ReactElement, ReactNode, useState } from "react";
import {
  Button,
  ButtonProps,
  IconButton,
  IconButtonProps,
  LinkButton,
  LinkButtonProps,
} from "./Button";
import { Icon, IconProps } from "./Icon";
import { classNames } from "@/utils/classNames";
import { renderIcon } from "./iconHelpers";
import { IconName } from "./iconMapping";
import { useDispatch, useMouseEventHandler, useTranslation } from "@/stores/RootStoreContext";
import { isTMessage, isTMessageParam, TMessage, TMessageParam } from "@/i18n/tt";
import { isFunction } from "lodash";
import { DCEvent } from "@/events/eventsDomain";
import { TextInput } from "./TextInput";
import { isArrowDown, isArrowUp, isEnter, isEscape } from "@/utils/keyboardEvents";
import { Tag, TagProps } from "./Tag";
import { emptyFileUploadConfig, FileUploadConfig } from "@/fileUpload/fileUploadDomain";
import { SelectFilesButtonProps, SelectFilesInput } from "./SelectFilesButton";
import { Markdown } from "@/text/Markdown.tsx";
import { Menu as BaseMenu } from "@base-ui/react/menu";
import { VBox } from "@/components-v2/Box.tsx";
import { Alert, AlertProps } from "@/components-v2/Alert.tsx";
import defer from "lodash/defer";

export const DropdownLinkButton = forwardRef<
  HTMLButtonElement,
  { isOpen: boolean } & Omit<LinkButtonProps, "type" | "arrow">
>(({ isOpen, onClick, ...props }, ref) => {
  const clickHandler = useMouseEventHandler(onClick);
  return (
    <LinkButton
      ref={ref}
      {...props}
      arrow={!props.icon && (isOpen ? "up" : "down")}
      onClick={(event) => {
        event.stopPropagation();
        clickHandler?.(event);
      }}
    />
  );
});

export const DropdownIconButton = forwardRef<
  HTMLButtonElement,
  { tooltip?: string | TMessage } & Omit<IconButtonProps, "type" | "tooltip">
>(({ tooltip, onClick, ...props }, ref) => {
  const { t } = useTranslation();
  const clickHandler = useMouseEventHandler(onClick);
  return (
    <IconButton
      ref={ref}
      {...props}
      tooltip={isTMessage(tooltip) ? t(tooltip) : (tooltip ?? t("open-menu"))}
      onClick={(event) => {
        event.stopPropagation();
        clickHandler?.(event);
      }}
    />
  );
});

export const DropdownButton = forwardRef<
  HTMLButtonElement,
  { isOpen: boolean } & Omit<ButtonProps, "type" | "arrow">
>(({ isOpen, onClick, ...props }, ref) => {
  const clickHandler = useMouseEventHandler(onClick);
  return (
    <Button
      ref={ref}
      {...props}
      arrow={props.label && !props.icon ? (isOpen ? "up" : "down") : undefined}
      onClick={(event) => {
        event.stopPropagation();
        clickHandler?.(event);
      }}
    />
  );
});

export const DropdownSelectButton = forwardRef<
  HTMLButtonElement,
  { isOpen: boolean; label?: string } & HTMLAttributes<HTMLButtonElement>
>(({ isOpen, label, className, ...props }, ref) => (
  <button
    ref={ref}
    type={"button"}
    {...props}
    className={classNames(
      className,
      "flex min-w-48 max-w-full items-center gap-2 px-4 py-3 border rounded-sm",
      "leading-tight text-left color-default bg-default border-gray cursor-pointer outline-hidden",
      "hover:border-gray-hard focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary",
    )}
  >
    <span className={"flex-1 truncate"}>{label ?? <>&nbsp;</>}</span>
    <Icon name={isOpen ? "caret-up" : "caret-down"} className={"text-xl leading-5 h-5 flex-none"} />
  </button>
));

const placeholderIcon: IconProps = { name: "info", className: "invisible" };

function renderMenuItemIcon({
  icon,
  menuHasIcons,
}: {
  icon?: IconName | IconProps;
  menuHasIcons: boolean;
}) {
  return icon ? renderIcon(icon) : menuHasIcons ? renderIcon(placeholderIcon) : undefined;
}

export type DropdownMenuItemProps = {
  type: "item";
  label: TMessage | TMessageParam | ReactNode;
  tag?: TagProps;
  categoryTag?: TMessage | TMessageParam | ReactNode;
  icon?: IconName | IconProps;
  id?: string;
  color?: "default" | "error";
  onSelect: (() => void) | DCEvent[];
  isDisabled?: boolean;
  tooltip?: TMessage | TMessageParam | string;
};

export type DropdownMenuInfoProps = {
  type: "info-item";
  infoText?: TMessage | string | ReactNode;
  alert?: AlertProps;
};

function DropdownMenuInfo({ infoText, alert }: Omit<DropdownMenuInfoProps, "type">) {
  const { tf } = useTranslation();
  return (
    <>
      {alert ? (
        <VBox padding={"xs"} className={"w-96"}>
          <Alert {...alert} />
        </VBox>
      ) : (
        <div className={classNames("py-4 px-2 text-center max-w-(--container-xs)")}>
          {isTMessage(infoText) || isTMessageParam(infoText) ? (
            tf(infoText)
          ) : (
            <Markdown value={infoText as string} />
          )}
        </div>
      )}
    </>
  );
}

function DropdownMenuItem({
  label,
  tag,
  color = "default",
  onSelect,
  isDisabled,
  tooltip,
  ...props
}: { menuHasIcons: boolean } & Omit<DropdownMenuItemProps, "type">) {
  const { tf } = useTranslation();
  const dispatch = useDispatch();
  return (
    <BaseMenu.Item
      disabled={isDisabled}
      onClick={(event) => {
        event.stopPropagation();
        if (!isDisabled) {
          if (isFunction(onSelect)) {
            onSelect();
          } else {
            dispatch(onSelect);
          }
        }
      }}
      render={(itemProps, state) => (
        <div
          {...itemProps}
          className={classNames(
            "flex",
            "gap-2",
            "items-center",
            "py-2",
            "px-4",
            isDisabled ? "cursor-default" : "cursor-pointer",
            isDisabled && "opacity-25",
            "min-w-40",
            color === "error" ? "color-error" : "color-default",
            state.highlighted && (color === "error" ? "bg-error" : "bg-primary-light"),
          )}
          data-tooltip={isTMessage(tooltip) || isTMessageParam(tooltip) ? tf(tooltip) : tooltip}
        >
          {renderMenuItemIcon(props)}
          <div className={"grow flex gap-2"}>
            {isTMessage(label) || isTMessageParam(label) ? tf(label) : label}
            {tag && <Tag {...tag} />}
          </div>
          {props.categoryTag && (
            <div className={classNames("ml-7", "subtle")}>
              {isTMessage(props.categoryTag) || isTMessageParam(props.categoryTag)
                ? tf(props.categoryTag)
                : props.categoryTag}
            </div>
          )}
        </div>
      )}
    />
  );
}

export type DropdownMenuCustomItemProps = {
  type: "custom-item";
  render: (props: { active: boolean; props: HTMLAttributes<HTMLElement> }) => ReactElement;
  canSelect?: boolean;
  onSelect?: () => void;
};

function DropdownMenuCustomItem({
  render,
  canSelect = true,
  onSelect,
}: Omit<DropdownMenuCustomItemProps, "type">) {
  return (
    <BaseMenu.Item
      disabled={!canSelect}
      onClick={(event) => {
        event.stopPropagation();
        if (canSelect) {
          onSelect?.();
        }
      }}
      className={({ highlighted, disabled }) =>
        disabled ? undefined : classNames("cursor-pointer", highlighted && "bg-primary-light")
      }
      render={(props, state) => render({ active: state.highlighted, props })}
    />
  );
}

export type DropdownMenuRadioItemProps = Omit<DropdownMenuItemProps, "type" | "icon"> & {
  type: "radio-item";
  isSelected?: boolean;
};

function DropdownMenuRadioItem({
  isSelected,
  ...props
}: { menuHasIcons: boolean } & Omit<DropdownMenuRadioItemProps, "type">) {
  return (
    <DropdownMenuItem {...props} icon={isSelected ? { name: "dot", weight: "fill" } : undefined} />
  );
}

export type DropdownMenuSeparatorProps = { type: "separator" };

function DropdownMenuSeparator() {
  return (
    <div className={"py-sm"}>
      <hr />
    </div>
  );
}

export type DropdownMenuLabelProps = {
  type: "label";
  label: TMessage | ReactNode;
  icon?: IconName | IconProps;
};

function DropdownMenuLabel({
  label,
  icon,
  gapAboveLabels,
  index,
}: { menuHasIcons: boolean; gapAboveLabels?: boolean; index: number } & Omit<
  DropdownMenuLabelProps,
  "type"
>) {
  const { t } = useTranslation();
  return (
    <div
      className={classNames(
        "flex",
        "gap-2",
        "items-center",
        gapAboveLabels && index > 0 ? "pt-8" : "pt-2",
        "px-4",
        "cursor-default",
        "min-w-40",
      )}
    >
      {icon ? renderIcon(icon) : undefined}
      <div className={"text-xs subtitle"}>{isTMessage(label) ? t(label) : label}</div>
    </div>
  );
}

export type DropdownMenuFileUploadProps = {
  type: "file-upload";
  label: DropdownMenuItemProps["label"];
  onSelect: SelectFilesButtonProps["onChange"];
  fileUploadConfig: FileUploadConfig;
};

export const DropdownMenuFileUpload = ({
  label,
  menuHasIcons,
  openFilePicker,
}: { menuHasIcons: boolean; openFilePicker: () => void } & Omit<
  DropdownMenuFileUploadProps,
  "type"
>) => {
  return (
    <DropdownMenuItem
      icon={"upload"}
      label={label}
      onSelect={openFilePicker}
      menuHasIcons={menuHasIcons}
    />
  );
};

export type DropdownMenuItemType =
  | DropdownMenuItemProps
  | DropdownMenuCustomItemProps
  | DropdownMenuRadioItemProps
  | DropdownMenuSeparatorProps
  | DropdownMenuLabelProps
  | DropdownMenuFileUploadProps
  | DropdownMenuInfoProps;

export type DropdownMenuItems = DropdownMenuItemType[];

function renderDropdownButton(
  button: DropdownMenuProps["button"],
  { isOpen }: { isOpen: boolean },
  props: HTMLAttributes<HTMLButtonElement>,
) {
  switch (typeof button) {
    case "string":
      return <DropdownLinkButton {...(props as LinkButtonProps)} isOpen={isOpen} label={button} />;
    case "function":
      return button({ isOpen, props });
    default:
      switch (button.type) {
        case "dotdotdot":
          return (
            <DropdownIconButton
              {...(props as IconButtonProps)}
              icon={{ name: isOpen ? "dotdotdot" : "dotdotdot-vertical", weight: "bold" }}
              tooltip={button.tooltip ?? { key: "open-menu" }}
              {...button}
            />
          );
        case "icon":
          return <DropdownIconButton {...button} {...(props as IconButtonProps)} />;
        case "link":
          return <DropdownLinkButton isOpen={isOpen} {...button} {...(props as LinkButtonProps)} />;
        case "button":
          return <DropdownButton isOpen={isOpen} {...button} {...(props as ButtonProps)} />;
        case "select":
          return <DropdownSelectButton isOpen={isOpen} label={button.label} {...props} />;
      }
  }
}

function renderDropdownItem(
  item: DropdownMenuItemType,
  index: number,
  menuHasIcons: boolean,
  gapAboveLabels: boolean | undefined,
  openFilePicker: (item: DropdownMenuFileUploadProps) => void,
) {
  switch (item.type) {
    case "item":
      return <DropdownMenuItem key={index} {...item} menuHasIcons={menuHasIcons} />;
    case "custom-item":
      return <DropdownMenuCustomItem key={index} {...item} />;
    case "radio-item":
      return <DropdownMenuRadioItem key={index} {...item} menuHasIcons={menuHasIcons} />;
    case "label":
      return (
        <DropdownMenuLabel
          key={index}
          {...item}
          index={index}
          menuHasIcons={menuHasIcons}
          gapAboveLabels={gapAboveLabels}
        />
      );
    case "separator":
      return <DropdownMenuSeparator key={index} {...item} />;
    case "file-upload":
      return (
        <DropdownMenuFileUpload
          key={index}
          {...item}
          menuHasIcons={menuHasIcons}
          openFilePicker={() => openFilePicker(item)}
        />
      );
    case "info-item":
      return <DropdownMenuInfo key={index} {...item} />;
  }
}

export type DropdownMenuProps = {
  button:
    | string
    | ({ type: "dotdotdot"; tooltip?: string | TMessage } & Omit<
        IconButtonProps,
        "type" | "icon" | "tooltip"
      >)
    | ({ type: "icon"; tooltip?: string | TMessage } & Omit<IconButtonProps, "type" | "tooltip">)
    | ({ type: "link" } & Omit<LinkButtonProps, "type" | "arrow">)
    | ({ type: "button" } & Omit<ButtonProps, "type" | "arrow">)
    | { type: "select"; label?: string }
    | ((props: { isOpen: boolean; props: HTMLAttributes<HTMLButtonElement> }) => ReactElement);
  items: Array<DropdownMenuItemType | null | undefined | false | "" | 0>;
  gapAboveLabels?: boolean;
  filter?: string;
  filterPlaceholder?: TMessage;
  onFilterChange?: (input: string) => void;
  alignment?: "start" | "center" | "end";
  disabled?: boolean;
};

export function DropdownMenu({
  button,
  items,
  gapAboveLabels,
  filter,
  filterPlaceholder,
  onFilterChange,
  alignment,
  ...props
}: DropdownMenuProps) {
  const [fileInputRef, setFileInputRef] = useState<HTMLInputElement | null>(null);
  const [activeFileInputItem, setActiveFileInputItem] = useState<DropdownMenuFileUploadProps>();
  const hasIcons = items.some(
    (item) =>
      item &&
      (item.type === "radio-item" ||
        (item.type === "item" && item.icon) ||
        (item.type === "label" && item.icon)),
  );
  const openFilePicker = (item: DropdownMenuFileUploadProps) => {
    setActiveFileInputItem(item);
    defer(() => fileInputRef?.click());
  };
  const hasFileUploadItem = items.some((x) => x && x.type === "file-upload");

  return (
    <BaseMenu.Root>
      <BaseMenu.Trigger
        disabled={props.disabled}
        onClick={(event) => {
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        render={(props, state) => renderDropdownButton(button, { isOpen: state.open }, props)}
      />
      {hasFileUploadItem && (
        <SelectFilesInput
          fileUploadConfig={activeFileInputItem?.fileUploadConfig ?? emptyFileUploadConfig()}
          onChange={(...args) => activeFileInputItem?.onSelect(...args)}
          setInputRef={setFileInputRef}
        />
      )}
      <BaseMenu.Portal>
        <BaseMenu.Positioner align={alignment}>
          <BaseMenu.Popup
            style={{
              minWidth: "var(--anchor-width)",
              maxHeight: "var(--available-height)",
              transformOrigin: "var(--transform-origin)",
            }}
            className={classNames(
              "rounded-sm",
              "border",
              "border-gray",
              "shadow-lg",
              "bg-default",
              "inline-flex",
              "flex-col",
              "py-1",
              "overflow-auto",
            )}
          >
            {filter !== undefined && (
              <DropdownMenuCustomItem
                canSelect={false}
                render={() => (
                  <div
                    className="px-2 py-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <TextInput
                      icon={"magnifying-glass"}
                      value={filter}
                      placeholder={filterPlaceholder}
                      onChange={(e) => onFilterChange?.(e.target.value)}
                      onKeyDown={(e) => {
                        if (!isArrowUp(e) && !isArrowDown(e) && !isEnter(e) && !isEscape(e)) {
                          e.stopPropagation();
                        }
                      }}
                    />
                  </div>
                )}
              />
            )}
            {items.map((item, index) =>
              item
                ? renderDropdownItem(item, index, hasIcons, gapAboveLabels, openFilePicker)
                : undefined,
            )}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
}
